
// date-utils.ts (or in your component file)
export function formatDate(
  inputDate: Date | number | string | null | undefined,
  locale: string = 'en-GB'
): string {
  // 1) Guard nullish, empty string, and explicit epoch zero
  if (inputDate === null || inputDate === undefined || inputDate === '' || inputDate === 0) {
    return '';
  }

  let date: Date | null = null;

  // 2) Numbers: handle epoch seconds vs millis
  if (typeof inputDate === 'number') {
    // Heuristic: seconds < 10_000_000_000
    const millis = inputDate < 10_000_000_000 ? inputDate * 1000 : inputDate;
    const d = new Date(millis);
    date = isNaN(d.getTime()) ? null : d;
  }

  // 3) Strings: support 'YYYY-MM-DD', ISO, and guard placeholders
  else if (typeof inputDate === 'string') {
    const s = inputDate.trim();

    // Treat common placeholders as empty
    if (!s || s === '0000-00-00') {
      return '';
    }

    // If strictly YYYY-MM-DD, construct via components to avoid timezone offsets
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = s.match(ymd);
    if (match) {
      const [, yearStr, monthStr, dayStr] = match;
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);

      const d = new Date(year, month - 1, day);
      date = isNaN(d.getTime()) ? null : d;
    } else {
      // Fallback: let Date parse ISO/string
      const d = new Date(s);
      date = isNaN(d.getTime()) ? null : d;
    }
  }

  // 4) Already a Date: validate
  else if (inputDate instanceof Date) {
    date = isNaN(inputDate.getTime()) ? null : inputDate;
  }

  // 5) Unsupported type
  else {
    return '';
  }

  if (!date) {
    return '';
  }

  // 6) Format using Intl to 'dd-MMM-yyyy' with hyphens (e.g., 09-Jan-2026)
  // Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  // returns '09 Jan 2026' → replace spaces with hyphens
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };

  // Use toLocaleDateString; ensure short month is capitalized as per locale
  const formatted = date.toLocaleDateString(locale, options).replace(/ /g, '-');

  return formatted;
}


export function formatDateTime(inputDate: Date | string) {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2,'0');
    const minutes = String(date.getMinutes()).padStart(2,'0');
    const seconds = String(date.getSeconds()).padStart(2,'0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

export function toDateOrNull(value: unknown): Date | null {
    // Null / undefined / empty string
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Numbers: epoch seconds or milliseconds
    if (typeof value === 'number') {
      const millis = value < 10_000_000_000 ? value * 1000 : value;
      const d = new Date(millis);
      return isNaN(d.getTime()) ? null : d;
    }

    // Strings: ISO or backend placeholders
    if (typeof value === 'string') {
      const s = value.trim();

      if (!s || s === '0000-00-00' || s === '1970-01-01') {
        return null;
      }

      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }

    // Already a Date
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    return null;
  }